import { types, cast, getEnv, flow, getRoot } from 'mobx-state-tree'
import { Environment } from '../environment'
import { UserApi } from '../../services/api-objects/UserApi'
import { Awaited } from '../../utility-types'
import { CardApi } from '../../services/api-objects/CardApi'
import { InfoCard } from '../card/InfoCard'

export const CardStoreModel = types
  .model('CardStore')
  .props({
    status: types.optional(types.enumeration(['idle', 'pending', 'done', 'error']), 'idle'),
    selectedCard: types.maybe(InfoCard),
    cards: types.frozen(),
  })
  .actions((self) => ({
    setStatus(value?: 'idle' | 'pending' | 'done' | 'error') {
      self.status = value || 'idle'
    },
    setCards(cards) {
      self.cards = cards
    },
    setSelectedCard(card: typeof self.selectedCard) {
      self.selectedCard = card
    },
    createNewCard() {
      self.selectedCard = InfoCard.create({ id: Math.random() * 999, touched: false })
    },
    reset() {
      self.selectedCard = undefined
      self.status = 'idle'
    },
  }))
  .views((self) => ({
    get environment() {
      return getEnv(self) as Environment
    },
    get rootStore(): any {
      return getRoot(self) as any
    },
    get cardNames(): any {
      if (self.cards) {
        return self.cards.map((c) => c.card_name.replace(/\s+/g, '')?.toLowerCase())
      }
      return []
    },
  }))
  .actions((self) => ({
    loadCards: flow(function* () {
      try {
        self.setStatus('pending')
        const result: Awaited<ReturnType<typeof CardApi.getCards>> = yield CardApi.getCards(
          'cards',
          false,
          [],
          'list_cards',
        )
        if (result.ok && result.data) {
          self.setStatus('done')
          const cards = result.data.rows.map((r) => {
            const c = {}
            r.columns.map((col) => {
              c[col.field] = col.value
            })
            return { id: r.id, ...c }
          })
          self.setCards(cards)
          return cards
        }
      } catch (error) {
        self.setStatus('error')
        return false
      }
    }),
    createInfoCard: flow(function* (card) {
      try {
        self.setStatus('pending')
        const result: Awaited<ReturnType<typeof CardApi.createInfoCard>> =
          yield CardApi.createInfoCard({
            card_name: card.card_name,
            versions: [
              {
                language: 'en',
                card_json: card.card_json,
              },
            ],
          })
        if (result.ok && result.data) {
          self.setStatus('done')
        }
      } catch (error) {
        self.setStatus('error')
        return false
      }
    }),
    checkDeleteCard: flow(function* (card_id: number) {
      try {
        self.setStatus('pending')
        const result: Awaited<ReturnType<typeof CardApi.checkDeleteCard>> =
          yield CardApi.checkDeleteCard(card_id)
        if (result.ok && result.data) {
          self.setStatus('done')
          return result.data
        }
      } catch (error) {
        self.setStatus('error')
        return error
      }
    }),
    deleteCard: flow(function* (card_id: number) {
      try {
        self.setStatus('pending')
        const result: Awaited<ReturnType<typeof CardApi.deleteCard>> =
          yield CardApi.deleteCard(card_id)
        if (result.ok && result.data) {
          self.setStatus('done')
        }
      } catch (error) {
        self.setStatus('error')
        return false
      }
    }),
    getInfoCard: flow(function* (card_id: number) {
      try {
        self.setStatus('pending')
        const result: Awaited<ReturnType<typeof CardApi.getInfoCard>> =
          yield CardApi.getInfoCard(card_id)
        if (result.ok && result.data) {
          self.setSelectedCard({ ...result.data, touched: true })
          self.setStatus('done')
        }
      } catch (error) {
        self.setStatus('error')
        return false
      }
    }),
  }))
